import { formatUtcDateAsSwedishTimeZoneDate } from 'tradera/utils/date';
import { PAGE_LIST_TYPES } from '../../constants/list-types';
import { PaidStatuses } from 'tradera/constants/paid-statuses';

const isTakeaway = (shippingType) => {
    return shippingType === 'Takeaway' || shippingType === 'TakeawayOnly';
};

const getSoldItemStateFor = (sellerState) => {
    if (sellerState.shipped && sellerState.feedbackLeft) {
        return 'Done';
    }
    if (sellerState.shipped) {
        return 'Shipped';
    }
    return sellerState.paid ? 'Paid' : 'NotPaid';
};

export const initialPayloadItems = (data, listType, dayjs) => {
    return data.purchaseOrders
        .map((purchaseOrder) =>
            purchaseOrder.rows.map((row) => ({
                ...row,
                caller: {},
                haveMultipleRows: purchaseOrder.rows.length > 1,
                haveCombinedShipping:
                    !!purchaseOrder.combinedShippingInformation,
                key: row.listing.itemId,
                itemId: row.listing.itemId,
                itemUrl: row.listing.itemUrl,
                listing: {
                    ...row.listing,
                    restart: {},
                    endDate: {
                        formatted: formatUtcDateAsSwedishTimeZoneDate(
                            row.listing.endDate,
                            dayjs
                        )
                    }
                },
                bidInformation: {
                    bidCount: row.buying.bidCount
                },
                seller: {
                    ...purchaseOrder.sellerMember,
                    id: purchaseOrder.sellerMember.memberId
                },
                transaction: {
                    ...row.transaction,
                    ...(purchaseOrder.shipping.selectedShipping
                        ? {
                              selectedShipping: {
                                  ...purchaseOrder.shipping.selectedShipping,
                                  isTakeaway: isTakeaway(
                                      purchaseOrder.shipping.selectedShipping
                                          .shippingType
                                  )
                              }
                          }
                        : null),
                    ...(purchaseOrder.shipping.shippingDiscount
                        ? {
                              shippingDiscount: {
                                  ...purchaseOrder.shipping.shippingDiscount,
                                  isTakeaway: isTakeaway(
                                      purchaseOrder.shipping.shippingDiscount
                                          .shippingType
                                  )
                              }
                          }
                        : null),
                    ...(purchaseOrder.shipping.shipping?.url
                        ? {
                              trackingLink: purchaseOrder.shipping.shipping?.url
                          }
                        : null),
                    bookedShipment: {
                        ...purchaseOrder.shipping.shipping
                    },
                    paidShipmentCheckoutId:
                        purchaseOrder.shipping.shipping?.shipmentCheckoutId,
                    shipmentId: purchaseOrder.shipping.shipping?.shipmentId,
                    hasUnpaidShipping: purchaseOrder.state.hasUnpaidShipping,
                    isRefundAllowed: purchaseOrder.state.isRefundAllowed,
                    isDiscountAllowed: purchaseOrder.state.isDiscountAllowed,
                    isStartDisputeV2Allowed:
                        purchaseOrder.state.isStartDisputeV2Allowed,
                    paymentMethod: purchaseOrder.payment.paymentMethod,
                    paidStatus: purchaseOrder.state.paidStatus,
                    purchaseOrderId: purchaseOrder.purchaseOrderId,
                    hasBuyerProtection:
                        purchaseOrder.payment.hasBuyerProtection,
                    ...(listType === PAGE_LIST_TYPES.BUYER_PURCHASES
                        ? {
                              buyerState: {
                                  ...row.transaction.sellerState,
                                  ...row.transaction.buyerState,
                                  paid: [
                                      PaidStatuses.PAID,
                                      PaidStatuses.MARKED_AS_PAID_BY_BUYER,
                                      PaidStatuses.MARKED_AS_PAID_BY_SELLER,
                                      PaidStatuses.PAYMENT_PENDING
                                  ].includes(purchaseOrder.state.paidStatus),
                                  shippingBooked:
                                      purchaseOrder.state.isShippingBooked,
                                  shipped: purchaseOrder.state.isShipped,
                                  hidden: purchaseOrder.state.isHiddenForBuyer,
                                  inr: row.transaction.buyerState.isInrSubmitted
                              }
                          }
                        : {
                              sellerState: {
                                  upiSubmitted:
                                      purchaseOrder.state.isUpiSubmitted,
                                  dcsSubmitted:
                                      purchaseOrder.state.isDcsSubmitted,
                                  state: getSoldItemStateFor({
                                      ...row.transaction.sellerState,
                                      shipped: purchaseOrder.state.isShipped,
                                      paid: [
                                          PaidStatuses.PAID,
                                          PaidStatuses.MARKED_AS_PAID_BY_SELLER,
                                          PaidStatuses.PAYMENT_PENDING
                                      ].includes(purchaseOrder.state.paidStatus)
                                  }),
                                  ...row.transaction.sellerState,
                                  paid: [
                                      PaidStatuses.PAID,
                                      PaidStatuses.MARKED_AS_PAID_BY_SELLER,
                                      PaidStatuses.PAYMENT_PENDING
                                  ].includes(purchaseOrder.state.paidStatus),
                                  shippingBooked:
                                      purchaseOrder.state.isShippingBooked,
                                  shipped: purchaseOrder.state.isShipped,
                                  hidden: purchaseOrder.state.isHiddenForSeller
                              }
                          }),
                    transactionDate: {
                        formatted: formatUtcDateAsSwedishTimeZoneDate(
                            row.transaction.transactionDate,
                            dayjs
                        )
                    },
                    buyer: {
                        ...purchaseOrder.buyerMember,
                        id: purchaseOrder.buyerMember.memberId
                    },
                    dispute: row.transaction.dispute
                }
            }))
        )
        .flatMap((row) => row);
};

export const initialPayloadPurchaseOrders = (data, page) => {
    return data.purchaseOrders.map((purchaseOrder) => {
        return {
            ...purchaseOrder,
            shipping: {
                ...purchaseOrder.shipping,
                ...(purchaseOrder.shipping.selectedShipping
                    ? {
                          selectedShipping: {
                              ...purchaseOrder.shipping.selectedShipping,
                              isTakeaway: isTakeaway(
                                  purchaseOrder.shipping.selectedShipping
                                      .shippingType
                              )
                          }
                      }
                    : null),
                ...(purchaseOrder.shipping.shippingDiscount
                    ? {
                          shippingDiscount: {
                              ...purchaseOrder.shipping.shippingDiscount,
                              isTakeaway: isTakeaway(
                                  purchaseOrder.shipping.shippingDiscount
                                      .shippingType
                              )
                          }
                      }
                    : null)
            },
            transactionIds: purchaseOrder.rows.map((row) => {
                return row.transaction.transactionId;
            }),
            itemsToGiveFeedBackTo: purchaseOrder.rows
                .map((row) => {
                    if (
                        row.transaction[
                            page.listType === PAGE_LIST_TYPES.BUYER_PURCHASES
                                ? 'buyerState'
                                : 'sellerState'
                        ].feedbackLeft !== true
                    ) {
                        return row.transaction.transactionId;
                    }
                    return null;
                })
                .filter(Boolean)
        };
    });
};
